import { BottomSheetTextInput } from '@gorhom/bottom-sheet'
import { useMemo } from 'react'
import { Pressable, Text, TextInput, View } from 'react-native'

import { Typography } from '../text/Typography'
import { asBottomSheetRef } from './asBottomSheetRef'
import type { TInputProps } from './Input.type'
import { useInput } from './useInput'

const ASTERISK = '*'

export type TTextareaProps = Omit<TInputProps, 'startIcon' | 'endIcon'> & {
  numberOfLines?: number
  maxLength?: number
  maxHeight?: number
  blurOnSubmit?: boolean
  inBottomSheet?: boolean
}
export const Textarea = ({ ref, ...props }: TTextareaProps) => {
  const {
    isRequired,
    testID,
    numberOfLines,
    maxLength,
    value,
    blurOnSubmit,
    autofocus,
    maxHeight,
    textAlignVertical,
    style,
    inBottomSheet,
  } = props

  const {
    label,
    description,
    errorMessage,
    getBaseProps,
    getLabelProps,
    getInputWrapperProps,
    getInputProps,
    getMainWrapperProps,
    getHelperWrapperProps,
    getDescriptionProps,
    getErrorMessageProps,
  } = useInput({
    isDisabled: props.isDisabled === undefined ? false : props.isDisabled,
    ref,
    isInput: false,
    ...props,
  })

  const labelContent = useMemo(
    () =>
      label ? (
        <Typography {...getLabelProps()}>
          {label} {!!isRequired && <Text className="text-danger-text-contrast-default">{ASTERISK}</Text>}
        </Typography>
      ) : null,
    [getLabelProps, isRequired, label],
  )

  const helperWrapper = useMemo(() => {
    if (!errorMessage && !description) return null

    return (
      <View {...getHelperWrapperProps()}>
        {description != null && <Typography {...getDescriptionProps()}>{description}</Typography>}
        {errorMessage != null && <Typography {...getErrorMessageProps()}>{errorMessage}</Typography>}
      </View>
    )
  }, [errorMessage, description, getHelperWrapperProps, getErrorMessageProps, getDescriptionProps])

  const inputWrapper = useMemo(() => {
    if (inBottomSheet) {
      const { ref: bottomSheetInputRef, ...inputProps } = getInputProps()
      return (
        <View {...getInputWrapperProps()}>
          <BottomSheetTextInput
            ref={asBottomSheetRef(bottomSheetInputRef)}
            {...inputProps}
            //FIXME: autofocus should not be enabled for accessibility
            // oxlint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={autofocus}
            blurOnSubmit={blurOnSubmit}
            testID={testID}
            value={value}
            style={[maxHeight ? { maxHeight } : undefined, style]}
            multiline
            numberOfLines={numberOfLines}
            maxLength={maxLength}
            textAlignVertical={textAlignVertical ?? 'top'} // explanation here : https://github.com/facebook/react-native/issues/13897
          />
        </View>
      )
    }
    return (
      <View {...getInputWrapperProps()}>
        <TextInput
          {...getInputProps()}
          //FIXME: autofocus should not be enabled for accessibility
          // oxlint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autofocus}
          blurOnSubmit={blurOnSubmit}
          testID={testID}
          value={value}
          style={[maxHeight ? { maxHeight } : undefined, style]}
          multiline
          numberOfLines={numberOfLines}
          maxLength={maxLength}
          textAlignVertical={textAlignVertical ?? 'top'} // explanation here : https://github.com/facebook/react-native/issues/13897
        />
      </View>
    )
  }, [
    getInputProps,
    maxHeight,
    blurOnSubmit,
    getInputWrapperProps,
    autofocus,
    numberOfLines,
    testID,
    maxLength,
    value,
    textAlignVertical,
    style,
    inBottomSheet,
  ])

  const mainWrapper = useMemo(() => {
    return (
      <>
        <Pressable {...getMainWrapperProps()}>
          {labelContent}
          {inputWrapper}
        </Pressable>
        {helperWrapper}
      </>
    )
  }, [getMainWrapperProps, helperWrapper, inputWrapper, labelContent])

  return <View {...getBaseProps()}>{mainWrapper}</View>
}
